/**
 * Carriers on the Planning screen: put drones aboard a boat, choose where on
 * the boat's route each one launches, and recover them afterwards.
 *
 * The launching is not done here. The predecessor armed and launched from a
 * browser effect, so a carrier only worked while somebody had this screen open,
 * and two open screens launched twice. This panel saves a plan; the server's
 * executor watches the boat and launches when it gets there, and the plan comes
 * back over the socket as it changes.
 *
 * A boat is a vehicle reporting MAV_TYPE_SURFACE_BOAT (11). Nothing here asks
 * whether it is simulated: a practice boat and a real one get the same panel.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useShallow } from 'zustand/react/shallow';

import {
  clearCarrierPlan,
  launchFromCarrier,
  recoverToCarrier,
  setCarrierPlan,
  type CarrierPhase,
  type CarrierPlan,
  type EmbarkRequest,
} from '@/api/carriers';
import { haversineM, useMissionStore, useUploadedMission } from '@/stores/missionStore';
import { aboardCount, phaseOf, useCarrierPlans, useCarrierStore } from '@/stores/carrierStore';
import { useDroneIdentity, useDroneScalars, useDroneStore } from '@/stores/droneStore';
import type { DroneId, Waypoint } from '@/types/domain';

const MAV_TYPE_SURFACE_BOAT = 11;
const NO_ROUTE: Waypoint[] = [];
const DEFAULT_LAUNCH_ALT_M = 15;
const OK = 'oklch(0.78 0.15 155)';
const WARN = 'oklch(0.80 0.15 85)';
const MUTED = 'rgba(244,244,245,0.45)';

const PHASE_LABEL: Record<CarrierPhase, { label: string; color: string }> = {
  aboard: { label: 'ABOARD', color: WARN },
  launching: { label: 'LAUNCHING', color: WARN },
  airborne: { label: 'AIRBORNE', color: OK },
  landed: { label: 'LANDED', color: MUTED },
};

/** Boats this operator sees in full, and every aircraft that could go aboard one. */
function useCarrierCandidates(): { boats: DroneId[]; others: DroneId[] } {
  const rows = useDroneStore(
    useShallow((state) =>
      state.ids
        .filter((id) => state.drones.get(id)?.access !== 'map')
        .map((id) => `${id}\u0000${state.drones.get(id)?.scalars.mavType === MAV_TYPE_SURFACE_BOAT ? 'b' : 'o'}`),
    ),
  );
  return useMemo(() => {
    const boats: DroneId[] = [];
    const others: DroneId[] = [];
    for (const row of rows) {
      const [id, kind] = row.split('\u0000') as [DroneId, string];
      (kind === 'b' ? boats : others).push(id);
    }
    return { boats, others };
  }, [rows]);
}

function Callsign({ droneId }: { droneId: string }) {
  const identity = useDroneIdentity(droneId);
  return <>{identity?.callsign ?? droneId}</>;
}

function SmallButton({
  children,
  onClick,
  disabled,
  tone,
  label,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tone?: string;
  label?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="rounded-full border px-2.5 py-[3px] font-mono text-[8.5px] font-bold tracking-wide disabled:opacity-40"
      style={{
        color: tone ?? 'rgba(244,244,245,0.7)',
        borderColor: tone ? `color-mix(in oklch, ${tone} 50%, transparent)` : 'rgba(255,255,255,0.15)',
      }}
    >
      {children}
    </button>
  );
}

/** Under way or stopped, and how far the next launch point is. */
function BoatState({ boatId, plan }: { boatId: string; plan: CarrierPlan | null }) {
  const { lat, lon, armed } = useDroneScalars(boatId, ['lat', 'lon', 'armed']);
  const fired = new Set(plan?.fired ?? []);
  const next = plan?.embarked
    .filter((e) => phaseOf(plan, e.droneId) === 'aboard' && !fired.has(e.deployIndex))
    .sort((a, b) => a.deployIndex - b.deployIndex)[0];
  const distance =
    next && typeof lat === 'number' && typeof lon === 'number'
      ? haversineM(lat, lon, next.deployLat, next.deployLon)
      : null;
  return (
    <span className="font-mono text-[9px] text-white/45">
      {armed ? 'UNDER WAY' : 'STOPPED'}
      {next && distance !== null ? ` · ${Math.round(distance)} M TO WP ${next.deployIndex + 1}` : ''}
    </span>
  );
}

function AboardForm({
  boatId,
  plan,
  candidates,
  onMessage,
}: {
  boatId: string;
  plan: CarrierPlan | null;
  candidates: DroneId[];
  onMessage: (text: string, warn?: boolean) => void;
}) {
  const route = useUploadedMission(boatId) ?? [];
  const [droneId, setDroneId] = useState<string>('');
  const [deployIndex, setDeployIndex] = useState(0);
  const [alt, setAlt] = useState(String(DEFAULT_LAUNCH_ALT_M));
  const [busy, setBusy] = useState(false);

  const choice = droneId && candidates.includes(droneId as DroneId) ? droneId : (candidates[0] ?? '');
  // A shared empty route: a fresh [] from the selector is a new snapshot on
  // every read, and React re-renders on it until it gives up.
  const mission = useMissionStore((state) => (choice ? state.draft[choice] : undefined)) ?? NO_ROUTE;

  if (route.length === 0) {
    return (
      <p className="mt-2 text-[10px] leading-relaxed text-white/45">
        Upload this boat's route first. Launch points are waypoints on the route the boat holds.
      </p>
    );
  }
  if (candidates.length === 0) {
    return (
      <p className="mt-2 text-[10px] leading-relaxed text-white/45">
        No aircraft free to go aboard. Each one can ride only one boat.
      </p>
    );
  }

  const putAboard = async () => {
    const altM = Number(alt);
    if (!Number.isFinite(altM)) {
      onMessage('Launch altitude must be a number of metres', true);
      return;
    }
    const keep: EmbarkRequest[] = (plan?.embarked ?? []).map((e) => ({
      droneId: e.droneId,
      deployIndex: e.deployIndex,
      launchAltM: e.launchAltM,
      mission: e.mission,
    }));
    setBusy(true);
    const result = await setCarrierPlan(boatId, [
      ...keep,
      { droneId: choice, deployIndex, launchAltM: altM, mission },
    ]);
    setBusy(false);
    if (!result.ok) {
      onMessage(result.error.message, true);
      return;
    }
    useCarrierStore.getState().apply(boatId, result.data.plan);
    setDroneId('');
    onMessage(`${choice} is aboard. It launches when the boat reaches waypoint ${deployIndex + 1}.`);
  };

  return (
    <div className="mt-2 flex flex-col gap-1.5 border-t border-white/8 pt-2">
      <div className="flex gap-1.5">
        <select
          value={choice}
          onChange={(event) => setDroneId(event.target.value)}
          aria-label="Aircraft to put aboard"
          className="well min-w-0 flex-1 rounded-[8px] px-2 py-1 font-mono text-[10px] outline-none"
        >
          {candidates.map((id) => (
            <option key={id} value={id}>
              {id}
            </option>
          ))}
        </select>
        <select
          value={Math.min(deployIndex, route.length - 1)}
          onChange={(event) => setDeployIndex(Number(event.target.value))}
          aria-label="Waypoint it launches at"
          className="well rounded-[8px] px-2 py-1 font-mono text-[10px] outline-none"
        >
          {route.map((wp, index) => (
            <option key={`${index}-${wp.lat}`} value={index} disabled={wp.lat === 0 && wp.lon === 0}>
              WP {index + 1}
            </option>
          ))}
        </select>
        <input
          value={alt}
          onChange={(event) => setAlt(event.target.value)}
          inputMode="decimal"
          aria-label="Launch altitude in metres"
          title="Launch altitude, metres"
          className="well w-[46px] rounded-[8px] px-1.5 py-1 text-right font-mono text-[10px] outline-none"
        />
      </div>
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 text-[9.5px] leading-snug text-white/40">
          {mission.length
            ? `Flies its draft route after launch, ${mission.length} waypoint${mission.length === 1 ? '' : 's'}.`
            : 'No draft route, so it will hold over the launch point.'}
        </span>
        <SmallButton onClick={() => void putAboard()} disabled={busy || !choice} tone={WARN}>
          {busy ? 'SAVING' : 'PUT ABOARD'}
        </SmallButton>
      </div>
    </div>
  );
}

function CarrierCard({
  boatId,
  plan,
  candidates,
  onMessage,
}: {
  boatId: string;
  plan: CarrierPlan | null;
  candidates: DroneId[];
  onMessage: (text: string, warn?: boolean) => void;
}) {
  const [busy, setBusy] = useState(false);

  const run = async (label: string, call: () => Promise<{ ok: boolean; error: { message: string } | null }>) => {
    setBusy(true);
    const result = await call();
    setBusy(false);
    if (!result.ok) onMessage(result.error?.message ?? `${label} failed`, true);
    else onMessage(label);
  };

  const remove = async (droneId: string) => {
    if (!plan) return;
    const rest = plan.embarked.filter((e) => e.droneId !== droneId);
    if (rest.length === 0) {
      await run(`Nothing aboard ${boatId} now`, () => clearCarrierPlan(boatId));
      return;
    }
    await run(`${droneId} taken off ${boatId}'s plan`, () =>
      setCarrierPlan(
        boatId,
        rest.map((e) => ({ droneId: e.droneId, deployIndex: e.deployIndex, launchAltM: e.launchAltM, mission: e.mission })),
      ),
    );
  };

  const aboard = plan ? aboardCount(plan) : 0;

  return (
    <div data-testid={`carrier-${boatId}`} className="rounded-[12px] border border-white/8 bg-white/5 px-3 py-2">
      <div className="flex items-center gap-2 text-[11.5px]">
        <span className="min-w-0 truncate font-semibold">
          <Callsign droneId={boatId} />
        </span>
        {plan ? (
          <span
            className="rounded-full border px-2 py-[1px] font-mono text-[8.5px] font-bold"
            style={{ color: WARN, borderColor: `color-mix(in oklch, ${WARN} 45%, transparent)` }}
          >
            {aboard} ABOARD
          </span>
        ) : null}
        <span className="ml-auto">
          <BoatState boatId={boatId} plan={plan} />
        </span>
      </div>

      {plan ? (
        <ul className="mt-1.5 flex flex-col gap-1">
          {plan.embarked.map((entry) => {
            const phase = phaseOf(plan, entry.droneId);
            const { label, color } = PHASE_LABEL[phase];
            const message = plan.status[entry.droneId]?.message;
            return (
              <li key={entry.droneId} className="rounded-[10px] bg-black/20 px-2 py-1.5">
                <div className="flex items-center gap-1.5 text-[10.5px]">
                  <span className="min-w-0 truncate">
                    <Callsign droneId={entry.droneId} />
                  </span>
                  <span className="font-mono text-[8.5px] text-white/35">WP {entry.deployIndex + 1}</span>
                  <span className="ml-auto font-mono text-[8.5px] font-bold" style={{ color }}>
                    {label}
                  </span>
                </div>
                {message ? <p className="mt-0.5 text-[9.5px] leading-snug text-white/45">{message}</p> : null}
                <div className="mt-1 flex gap-1">
                  {phase === 'aboard' ? (
                    <SmallButton
                      disabled={busy}
                      onClick={() =>
                        void run(`Launching ${entry.droneId}`, () => launchFromCarrier(boatId, [entry.droneId]))
                      }
                    >
                      LAUNCH NOW
                    </SmallButton>
                  ) : null}
                  {phase === 'landed' ? (
                    <SmallButton
                      disabled={busy}
                      tone={OK}
                      onClick={() =>
                        void run(`${entry.droneId} recovered to ${boatId}`, () =>
                          recoverToCarrier(boatId, entry.droneId),
                        )
                      }
                    >
                      RECOVER
                    </SmallButton>
                  ) : null}
                  {phase === 'aboard' || phase === 'landed' ? (
                    <SmallButton
                      disabled={busy}
                      label={`Take ${entry.droneId} off this boat's plan`}
                      onClick={() => void remove(entry.droneId)}
                    >
                      ×
                    </SmallButton>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="mt-1 text-[10px] leading-relaxed text-white/40">
          Nothing aboard. Put drones on this boat and they launch by themselves when it reaches the
          waypoint you choose.
        </p>
      )}

      {plan && aboard > 0 ? (
        <div className="mt-1.5 flex gap-1.5">
          <SmallButton
            disabled={busy}
            tone={WARN}
            onClick={() => void run(`Launching everything aboard ${boatId}`, () => launchFromCarrier(boatId))}
          >
            LAUNCH ALL {aboard}
          </SmallButton>
        </div>
      ) : null}

      <AboardForm boatId={boatId} plan={plan} candidates={candidates} onMessage={onMessage} />
    </div>
  );
}

export function CarrierPanel({
  editingDrone,
  onMessage,
}: {
  editingDrone: DroneId | null;
  onMessage: (text: string, warn?: boolean) => void;
}) {
  const { boats, others } = useCarrierCandidates();
  const plans = useCarrierPlans();

  const planned = useMemo(() => new Map(plans.map((plan) => [plan.carrierId, plan])), [plans]);
  const taken = useMemo(() => new Set(plans.flatMap((plan) => plan.embarked.map((e) => e.droneId))), [plans]);
  const candidates = others.filter((id) => !taken.has(id));
  // The boat being edited first, so drawing its route and loading it sit together.
  const ordered = [...new Set([...boats, ...planned.keys()])].sort((a, b) =>
    a === editingDrone ? -1 : b === editingDrone ? 1 : a.localeCompare(b),
  );

  if (ordered.length === 0) return null;

  return (
    <div>
      <div className="label-mono mb-2 text-[9px]">CARRIER</div>
      <p className="mb-2 text-[10px] leading-relaxed text-white/40">
        Drones ride the boat and launch on their own when it reaches the waypoint you pick: the server
        arms each one, climbs it clear of the deck and starts its route.
      </p>
      <div className="flex flex-col gap-1.5">
        {ordered.map((boatId) => (
          <CarrierCard
            key={boatId}
            boatId={boatId}
            plan={planned.get(boatId) ?? null}
            candidates={candidates}
            onMessage={onMessage}
          />
        ))}
      </div>
    </div>
  );
}

export default CarrierPanel;
